/// <reference types="vite/client" />

/**
 * Mirrors CartServiceImpl.getEnvironmentPrefix() in ApiAcccess.service.ts
 * so console silencing follows the same host rules as API environment selection.
 */
function getEnvironmentPrefix(host: string): string {
  const subdomain = host.split('.')[0];

  if (subdomain.startsWith('www') && subdomain.length > 3) {
    const env = subdomain.substring(3);
    if (env === 'stage') {
      return 'stg';
    }
    return env;
  }

  return subdomain.split('-')[0];
}

/**
 * Same notion of “production” as initializeEnvironments():
 * production API defaults apply when NOT (envPrefix !== "store" && host !== "marketplace.nvidia.com").
 * Also keep logs on explicit dev/stg first-segment hosts (buyenv branch uses the same split).
 */
function isHostNvidiaProductionRuntime(): boolean {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false;
  }

  const host = window.location.hostname;
  const envPrefix = getEnvironmentPrefix(host);

  if (envPrefix !== 'store' && host !== 'marketplace.nvidia.com') {
    return false;
  }

  const firstSubSegment = host.split('.')[0].split('-')[0];
  if (firstSubSegment === 'dev' || firstSubSegment === 'stg') {
    return false;
  }

  return true;
}

/**
 * Silence noisy console methods only when the page is on NVIDIA production store/marketplace
 * (same host rules as ApiAcccess.initializeEnvironments). No import.meta — works for any bundle.
 * Outside the browser, falls back to NODE_ENV / Vite prod for tests or SSR.
 */
function isProductionRuntime(): boolean {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return isHostNvidiaProductionRuntime();
  }

  try {
    const env = import.meta.env;
    if (env != null && env.PROD === true) return true;
    if (env != null && env.MODE === 'production') return true;
  } catch {
    // import.meta unsupported
  }

  if (
    typeof process !== 'undefined' &&
    process.env &&
    process.env.NODE_ENV === 'production'
  ) {
    return true;
  }

  return false;
}

const noop = (): void => {};

if (isProductionRuntime() && typeof console !== 'undefined') {
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.trace = noop;
}
